/**
 * JobProgress — Phase 2.A presentational component.
 *
 * Renders a job's status badge, progress bar, current step, counters,
 * retry count, and (on terminal failure) the error message. Pure
 * presentation — pair with `useJobPolling` to drive it.
 *
 * Counters block is dynamic — we show whichever counters the worker
 * actually populated. The backend currently emits different counter
 * shapes per actor (CSV import vs PDF extraction vs reprice-fix),
 * and we don't want to hard-code one shape here.
 */
import React from "react";
import { Badge } from "./ui-extras";
import { CheckCircle, Warning, XCircle, Clock } from "@phosphor-icons/react";

const STATUS_VARIANT = {
  queued: "info",
  running: "info",
  retrying: "warning",
  completed: "success",
  failed: "error",
  cancelled: "warning",
};

const TERMINAL = new Set(["completed", "failed", "cancelled"]);

const COUNTER_LABELS = {
  rows_processed: "Rows processed",
  rows_total: "Rows total",
  inserted_count: "Inserted",
  duplicate_count: "Duplicates",
  failed_rows_count: "Failed rows",
  unmatched_count: "Unmatched",
  flagged_count: "Flagged",
  pages_processed: "Pages processed",
  total_pages: "Total pages",
  current_page: "Current page",
  invoices_generated: "Invoices generated",
  emails_sent: "Emails sent",
  emails_failed: "Emails failed",
  fixed_count: "Fixed",
};

export function JobProgress({ job, compact = false }) {
  if (!job) return null;
  const pct = Math.max(0, Math.min(100, job.progress_percent || 0));
  const status = job.status || "queued";
  const isTerminal = TERMINAL.has(status);
  const counters = job.counters || {};
  const counterEntries = Object.entries(counters)
    .filter(([k, v]) => COUNTER_LABELS[k] !== undefined && (v ?? null) !== null)
    .slice(0, 8);

  return (
    <div
      className="border border-zinc-200 p-3 bg-zinc-50"
      data-testid={`job-progress-${job.job_id}`}
    >
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Badge variant={STATUS_VARIANT[status] || "info"} data-testid={`job-status-${job.job_id}`}>
            {status.toUpperCase()}
          </Badge>
          {job.retry_count > 0 && (
            <span className="text-xs text-zinc-500">retry #{job.retry_count}</span>
          )}
        </div>
        <div className="text-xs text-zinc-500 font-mono">{job.job_id}</div>
      </div>

      {!isTerminal && (
        <>
          <div className="w-full bg-white border border-zinc-200 h-2 mb-2 overflow-hidden">
            <div
              className="bg-emerald-600 h-2 transition-all"
              style={{ width: `${pct}%` }}
              data-testid={`job-progress-bar-${job.job_id}`}
            />
          </div>
          <div className="flex justify-between text-xs text-zinc-600 mb-2">
            <span>{pct}%</span>
            <span className="font-mono">{job.current_step || "queued…"}</span>
          </div>
        </>
      )}

      {isTerminal && (
        <div className="text-xs mb-2 flex items-center gap-2">
          {status === "completed" && <><CheckCircle size={14} className="text-emerald-600"/> Done in {job.elapsed_ms ? `${(job.elapsed_ms/1000).toFixed(1)}s` : "—"}</>}
          {status === "failed" && <><XCircle size={14} className="text-red-600"/> Failed{job.error ? `: ${job.error}` : ""}</>}
          {status === "cancelled" && <><Warning size={14} className="text-amber-600"/> Cancelled</>}
        </div>
      )}

      {!compact && counterEntries.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2 text-xs">
          {counterEntries.map(([k, v]) => (
            <div key={k} className="bg-white border border-zinc-200 px-2 py-1" data-testid={`job-counter-${k}-${job.job_id}`}>
              <div className="text-zinc-500">{COUNTER_LABELS[k]}</div>
              <div className="font-mono text-zinc-900">{v ?? "—"}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function CompactJobLine({ job }) {
  if (!job) return null;
  const pct = Math.max(0, Math.min(100, job.progress_percent || 0));
  const status = job.status || "queued";
  return (
    <div className="flex items-center gap-2 text-xs" data-testid={`job-progress-compact-${job.job_id}`}>
      <Clock size={12}/>
      <span className="font-mono text-zinc-500">{job.job_id?.slice(-6)}</span>
      <Badge variant={STATUS_VARIANT[status] || "info"}>{status}</Badge>
      <div className="flex-1 bg-zinc-100 h-1 overflow-hidden">
        <div className="bg-emerald-600 h-1 transition-all" style={{ width: `${pct}%` }}/>
      </div>
      <span className="text-zinc-500">{pct}%</span>
    </div>
  );
}
